/**
 * Where preferences stop being data and start being the game.
 *
 * Every bootstrap that owns a piece of the running game binds to the store
 * here, so a setting changed in the hub is already in force by the time a run
 * starts — and one changed mid-session takes effect without a reload.
 *
 * The direction matters: `ui/settings` pushes into the engine, and the engine
 * never reaches back into the settings screen. That keeps the audio and input
 * layers testable without a modal, and it means a preference nothing consumes
 * is a dangling call rather than an invisible no-op.
 */

import type { AudioEngine } from '../../audio/audio-engine';
import type { OverShoulderCameraController } from '../../input/over-shoulder-camera-controller';
import { settingsStore } from './settings-store';
import type { GameplaySettings } from './settings-types';

/**
 * Keep an engine's buses on the player's mix for as long as it lives.
 *
 * Returns the unsubscribe, which every caller must run on teardown: the hub,
 * the arena and the dungeon each build their own AudioEngine, and a leaked
 * subscription would keep writing into a disposed context.
 */
export function bindAudioToSettings(engine: AudioEngine): () => void {
  return settingsStore.subscribe(({ audio }) => {
    engine.setMix({
      master: audio.master,
      sfx: audio.sfx,
      ambient: audio.ambient,
      ui: audio.ui,
      music: audio.music,
    });
    // The on/off is separate from the music bus level, so the mix alone never
    // silenced the bed (QA 2026-10-15: "Play music" off kept playing).
    engine.applyMusicEnabled(audio.musicEnabled);
  });
}

/** Same contract for the camera: sensitivity and invert, live. */
export function bindCameraToSettings(camera: OverShoulderCameraController): () => void {
  return settingsStore.subscribe(({ gameplay }) => {
    camera.setLookPreferences({
      sensitivity: gameplay.mouseSensitivity,
      invertLook: gameplay.invertLook,
    });
  });
}

/**
 * The current gameplay preferences, for the things that are read per-event
 * rather than configured once — whether to spawn a damage number, whether to
 * flash. Cheap: it is a field read and a spread, not a fetch.
 */
export function gameplayPreferences(): GameplaySettings {
  return settingsStore.get().gameplay;
}

/** Subscribe to gameplay changes alone, for callers that ignore the audio half. */
export function bindGameplayToSettings(fn: (g: GameplaySettings) => void): () => void {
  return settingsStore.subscribe(({ gameplay }) => fn(gameplay));
}
